import { ISpeechService } from './ISpeechService'
import { audioSettings } from '../AudioSettings/AudioSettings'
import { useCaptionStore } from '../../state/captionState'

const CAPTION_DWELL_BASE_MS = 300
const CAPTION_DWELL_MS_PER_CHAR = 55
const MIN_CAPTION_DWELL_MS = 1500
const MAX_CAPTION_DWELL_MS = 6500
const NO_START_MS = 4000
const MIN_WATCHDOG_MS = 6000
const MAX_WATCHDOG_MS = 45000
const WATCHDOG_MS_PER_CHAR = 120
const MAX_CONSECUTIVE_NO_STARTS = 2

/**
 * How long a caption-only line stays up: reading speed (~18 chars/s) with
 * a floor so one-word quips register and a ceiling so the longest
 * sentencing line does not stall the round. Fire TV's WebView has no
 * speech synthesis at all, so this IS the pacing of the whole game there.
 */
export const captionDwellMs = (text: string): number =>
  Math.min(
    Math.max(
      CAPTION_DWELL_BASE_MS + text.length * CAPTION_DWELL_MS_PER_CHAR,
      MIN_CAPTION_DWELL_MS,
    ),
    MAX_CAPTION_DWELL_MS,
  )

/**
 * Wraps the browser SpeechSynthesis API. Known quirks handled here:
 * - iOS Safari only speaks if the API was first used inside a user gesture,
 *   so unlock() must be called synchronously from the start-game click
 * - utterances can be garbage-collected before onend fires (kept in a Set)
 * - a hung utterance would freeze the game (no-start + watchdog timers)
 * - browsers with the API but no working voices degrade to timed dwells
 * - errors never reject: we log and resolve so the queue keeps flowing
 * Every line is also mirrored to the caption store for muted/SR players,
 * and caption-only dwells are cancelable exactly like spoken lines so a
 * skip or a kill lands instantly on every platform.
 */
export class SpeechService implements ISpeechService {
  private isInitialized = false
  private isUnlocked = false
  private consecutiveNoStarts = 0
  private activeUtterances = new Set<SpeechSynthesisUtterance>()
  private activeCancels = new Set<() => void>()

  public init() {
    if ('speechSynthesis' in window) {
      this.isInitialized = true
      // Clear anything left over from a previous page life.
      window.speechSynthesis.cancel()
      // When the player mutes, release any line that is mid-speech so the
      // queued scene transitions behind it keep flowing.
      audioSettings.subscribe((muted) => {
        if (muted) {
          this.cancelActive()
        }
      })
    } else {
      console.error('SpeechSynthesis is not supported in this browser.')
    }
  }

  /**
   * Must be called synchronously inside a user gesture (the splash click).
   * iOS Safari permanently blocks speech that was never gesture-primed.
   */
  public unlock(): void {
    if (!this.isInitialized || this.isUnlocked) return
    try {
      const primer = new SpeechSynthesisUtterance(' ')
      primer.volume = 0
      window.speechSynthesis.speak(primer)
      this.isUnlocked = true
    } catch (error) {
      console.warn('Speech unlock failed:', error)
    }
  }

  /** Ends the line in flight (spoken or caption-only) right now. */
  public cancelActive(): void {
    if (this.isInitialized) {
      window.speechSynthesis.cancel()
    }
    this.activeCancels.forEach((cancel) => cancel())
  }

  private get isDegraded(): boolean {
    return this.consecutiveNoStarts >= MAX_CONSECUTIVE_NO_STARTS
  }

  public isSilent(): boolean {
    return !this.isInitialized || this.isDegraded || audioSettings.isMuted()
  }

  private dwell(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        this.activeCancels.delete(finish)
        resolve()
      }
      const timer = setTimeout(finish, ms)
      this.activeCancels.add(finish)
    })
  }

  public async speak(text: string): Promise<void> {
    useCaptionStore.getState().setCaption(text)

    if (this.isSilent()) {
      await this.dwell(captionDwellMs(text))
      return
    }

    return new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text)
      this.activeUtterances.add(utterance)

      let settled = false
      let started = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(noStartTimer)
        clearTimeout(watchdog)
        this.activeUtterances.delete(utterance)
        this.activeCancels.delete(finish)
        resolve()
      }
      this.activeCancels.add(finish)

      // Voices that never start (present-but-broken speechSynthesis) should
      // fail fast, and after enough failures we stop trying entirely.
      const noStartTimer = setTimeout(() => {
        if (!started) {
          this.consecutiveNoStarts++
          console.warn(
            `Speech never started (${this.consecutiveNoStarts} in a row)`,
          )
          window.speechSynthesis.cancel()
          finish()
        }
      }, NO_START_MS)

      const watchdogMs = Math.min(
        Math.max(text.length * WATCHDOG_MS_PER_CHAR, MIN_WATCHDOG_MS),
        MAX_WATCHDOG_MS,
      )
      const watchdog = setTimeout(() => {
        console.warn(`Speech watchdog fired for: "${text.slice(0, 40)}..."`)
        window.speechSynthesis.cancel()
        finish()
      }, watchdogMs)

      utterance.onstart = () => {
        started = true
        this.consecutiveNoStarts = 0
      }
      utterance.onend = finish
      utterance.onerror = (event) => {
        // interrupted/canceled are normal when muting or navigating.
        if (event.error !== 'interrupted' && event.error !== 'canceled') {
          console.error('SpeechSynthesis error:', event.error)
        }
        finish()
      }

      window.speechSynthesis.speak(utterance)
    })
  }
}
